import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { Link } from 'react-router-dom';

function ProgrammierenWorkshop() {
    const { user, isMember, isAdmin } = useAuth();
    const [invoices, setInvoices] = useState([]);
    const [loadingInvoices, setLoadingInvoices] = useState(false);

    useEffect(() => {
        if (user && (isMember || isAdmin)) {
            fetchInvoices();
        }
    }, [user, isMember, isAdmin]);

    const fetchInvoices = async () => {
        setLoadingInvoices(true);
        try {
            const res = await fetch('/api/member/invoices', {
                headers: { 'Authorization': `Bearer ${user?.token}` }
            });
            const data = await res.json();
            if (data.data) {
                setInvoices(data.data);
            }
        } catch (err) {
            console.error('Fehler beim Laden der Rechnungen:', err);
        } finally {
            setLoadingInvoices(false);
        }
    };

    const downloadPDF = async (invoiceId, invoiceNumber) => {
        try {
            const res = await fetch(`/api/member/invoices/${invoiceId}/pdf`, {
                headers: { 'Authorization': `Bearer ${user?.token}` }
            });
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Rechnung_${invoiceNumber}.pdf`;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            alert('Fehler beim Download: ' + err.message);
        }
    };

    const formatCurrency = (amount) => {
        return parseFloat(amount || 0).toFixed(2) + ' EUR';
    };

    const getStatusBadge = (status) => {
        if (status === 'paid') return <span className="badge bg-success">Bezahlt</span>;
        return <span className="badge bg-warning">Offen</span>;
    };

    // Nicht eingeloggt
    if (!user) {
        return (
            <div className="text-center py-5">
                <div className="card mx-auto" style={{ maxWidth: '500px' }}>
                    <div className="card-body p-5">
                        <h2 className="mb-4">Ostercamp: Programmieren & Game Design</h2>
                        <p className="text-muted mb-4">
                            Dieser Bereich ist nur für angemeldete Mitglieder zugänglich.
                        </p>
                        <Link to="/login" className="btn btn-primary">
                            Jetzt anmelden
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    // Eingeloggt aber kein Mitglied
    if (!isMember && !isAdmin) {
        return (
            <div className="text-center py-5">
                <div className="card mx-auto" style={{ maxWidth: '500px' }}>
                    <div className="card-body p-5">
                        <h2 className="mb-4">Zugang nicht möglich</h2>
                        <p className="text-muted">
                            Dieser Bereich ist nur für Mitglieder zugänglich.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="programmieren-workshop">
            {/* Hero Header */}
            <div className="card mb-4" style={{
                background: 'linear-gradient(135deg, #ff6b35, #9b59b6, #324047)',
                color: 'white',
                border: 'none'
            }}>
                <div className="card-body text-center py-5">
                    <p className="mb-2" style={{ opacity: 0.8, fontSize: '1.1rem' }}>Ostercamp 2026 - 2. Woche</p>
                    <h1 className="mb-3" style={{ color: 'white' }}>Programmieren & Game Design</h1>
                    <div className="d-flex justify-content-center gap-3 flex-wrap">
                        <span className="badge" style={{ background: 'rgba(255,255,255,0.2)', fontSize: '0.9rem' }}>
                            7-14 Jahre
                        </span>
                        <span className="badge" style={{ background: 'rgba(255,255,255,0.2)', fontSize: '0.9rem' }}>
                            4 Tage
                        </span>
                        <span className="badge" style={{ background: 'rgba(255,255,255,0.2)', fontSize: '0.9rem' }}>
                            09:00 - 13:00 Uhr
                        </span>
                    </div>
                </div>
            </div>

            {/* Wichtige Info zur Altersgruppe */}
            <div className="alert mb-4" style={{ background: '#fff3e0', borderLeft: '4px solid #ff6b35' }}>
                <h5 className="alert-heading" style={{ color: '#e65100' }}>Gut zu wissen</h5>
                <p className="mb-0">
                    Da wir eine gemischte Altersgruppe haben, passe ich die Aufgaben täglich an – so wird niemand über- oder unterfordert.
                    Ihr bekommt jeden Abend einen kurzen Update, was wir gemacht haben und wie ich den nächsten Tag gestalte.
                </p>
            </div>

            {/* Rechnungen - nur für Mitglieder mit Rechnungen */}
            {invoices.length > 0 && (
                <div className="card mb-4" style={{ borderLeft: '4px solid #00d084' }}>
                    <div className="card-header d-flex justify-content-between align-items-center" style={{ background: 'rgba(0,208,132,0.1)' }}>
                        <h4 className="mb-0" style={{ color: '#00d084' }}>Deine Rechnungen</h4>
                    </div>
                    <div className="card-body">
                        <div className="table-responsive">
                            <table className="table table-hover mb-0">
                                <thead>
                                    <tr>
                                        <th>Rechnungs-Nr.</th>
                                        <th>Kind</th>
                                        <th>Betrag</th>
                                        <th>Status</th>
                                        <th>Aktion</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoices.map(invoice => (
                                        <tr key={invoice.id}>
                                            <td><strong>{invoice.invoice_number}</strong></td>
                                            <td>{invoice.child_name || '-'}</td>
                                            <td>
                                                {formatCurrency(invoice.total)}
                                                {invoice.discount > 0 && (
                                                    <small className="text-success d-block">
                                                        inkl. {invoice.discount_reason || 'Rabatt'}
                                                    </small>
                                                )}
                                            </td>
                                            <td>{getStatusBadge(invoice.status)}</td>
                                            <td>
                                                <button
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={() => downloadPDF(invoice.id, invoice.invoice_number)}
                                                >
                                                    PDF
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="mt-3 p-3 rounded" style={{ background: '#f5f5f0' }}>
                            <strong>Zahlungsinformationen:</strong><br/>
                            Kontoinhaber: Futurhinos<br/>
                            IBAN: DE66 5001 0517 6000 5878 84<br/>
                            BIC: INGDDEFFXXX<br/>
                            <small className="text-muted">Bitte die Rechnungsnummer als Verwendungszweck angeben.</small>
                        </div>
                    </div>
                </div>
            )}

            {/* Wichtige Infos */}
            <div className="card mb-4" style={{ borderLeft: '4px solid #ff6b35' }}>
                <div className="card-header" style={{ background: 'rgba(255,107,53,0.1)' }}>
                    <h4 className="mb-0" style={{ color: '#ff6b35' }}>Wichtige Infos zum Camp</h4>
                </div>
                <div className="card-body">
                    <div className="row g-4">
                        <div className="col-md-4">
                            <h6><strong>Wann & Wo</strong></h6>
                            <p className="mb-2">
                                <strong>Datum:</strong> 06.04. - 09.04.2026<br/>
                                <strong>Zeit:</strong> 09:00 - 13:00 Uhr
                            </p>
                            <p className="mb-0">
                                <strong>Adresse:</strong><br/>
                                Futurhinos for Kids<br/>
                                Gottlieb-Daimler-Str. 36<br/>
                                73614 Schorndorf
                            </p>
                        </div>
                        <div className="col-md-4">
                            <h6><strong>Was mitbringen?</strong></h6>
                            <ul className="mb-0">
                                <li>Vesper & Getränk für die Pause</li>
                                <li>Geld für Mittagessen (10-15 Euro)</li>
                                <li>Bequeme Kleidung</li>
                                <li>Optional: Buch o.ä. für die Randzeiten</li>
                            </ul>
                            <p className="mt-2 mb-0">
                                <small className="text-muted">
                                    Laptops werden von uns gestellt.
                                    Eigene Geräte können gerne mitgebracht werden.
                                </small>
                            </p>
                        </div>
                        <div className="col-md-4">
                            <h6><strong>Kontakt</strong></h6>
                            <p className="mb-2">
                                <strong>Email:</strong><br/>
                                <a href="mailto:info@futurhinos.de">info@futurhinos.de</a>
                            </p>
                            <p className="mb-0">
                                <strong>Telefon:</strong><br/>
                                <a href="tel:+4915129464490">0151 2946 4490</a><br/>
                                <small className="text-muted">(auch WhatsApp)</small>
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Randzeiten Info */}
            <div className="alert mb-4" style={{ background: '#fff3e0', borderLeft: '4px solid #ff9800' }}>
                <h5 className="alert-heading" style={{ color: '#e65100' }}>Randzeiten: 8:00 - 9:00 Uhr & 13:00 - 14:00 Uhr</h5>
                <p className="mb-2">
                    Ihr könnt eure Kinder bereits <strong>ab 8:00 Uhr</strong> bringen und <strong>bis 14:00 Uhr</strong> abholen.
                    Das ist allerdings keine Betreuung im klassischen Sinne - wir sind da und kümmern uns bei Fragen,
                    aber für die Beschäftigung in dieser Zeit sind die Kinder selbst verantwortlich.
                </p>
                <p className="mb-0">
                    <strong>Tipp:</strong> Bringt gerne ein Buch oder ähnliches mit. Wir haben auch Spielsachen da und Rückzugsmöglichkeiten.
                </p>
            </div>

            {/* Mittagessen */}
            <div className="card mb-4" style={{ borderLeft: '4px solid #ff6b35' }}>
                <div className="card-header" style={{ background: 'rgba(255,107,53,0.1)' }}>
                    <h4 className="mb-0" style={{ color: '#ff6b35' }}>Mittagessen</h4>
                </div>
                <div className="card-body">
                    <p className="mb-3">Wir bestellen gemeinsam Mittagessen. Bitte gebt eurem Kind entsprechend Geld mit (ca. 10-15 Euro pro Tag).</p>
                    <div className="row g-3">
                        <div className="col-6 col-md-3">
                            <div className="p-3 rounded text-center" style={{ background: '#f5f5f0' }}>
                                <strong>Montag</strong><br/>
                                <span className="text-muted">wird noch entschieden</span>
                            </div>
                        </div>
                        <div className="col-6 col-md-3">
                            <div className="p-3 rounded text-center" style={{ background: '#f5f5f0' }}>
                                <strong>Dienstag</strong><br/>
                                <span className="text-muted">wird noch entschieden</span>
                            </div>
                        </div>
                        <div className="col-6 col-md-3">
                            <div className="p-3 rounded text-center" style={{ background: '#f5f5f0' }}>
                                <strong>Mittwoch</strong><br/>
                                <span className="text-muted">wird noch entschieden</span>
                            </div>
                        </div>
                        <div className="col-6 col-md-3">
                            <div className="p-3 rounded text-center" style={{ background: '#f5f5f0' }}>
                                <strong>Donnerstag</strong><br/>
                                <span className="text-muted">wird noch entschieden</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tagesübersicht */}
            <div className="row g-4 mb-4">
                <div className="col-12">
                    <h2 className="mb-3" style={{ color: '#ff6b35' }}>Was erwartet euer Kind?</h2>
                </div>

                {/* Tag 1 */}
                <div className="col-md-6 col-lg-3">
                    <a href="/ostercamp-programmieren/tag-1/" className="text-decoration-none">
                        <div className="card h-100 hover-lift">
                            <div className="card-header text-white" style={{ background: '#ff6b35' }}>
                                <strong>Tag 1</strong>
                            </div>
                            <div className="card-body">
                                <h5 className="card-title" style={{ color: '#324047' }}>Erste Schritte</h5>
                                <ul className="list-unstyled mb-0" style={{ color: '#324047' }}>
                                    <li className="mb-2">PC-Grundlagen (Maus & Tastatur)</li>
                                    <li className="mb-2">Was ist Programmieren?</li>
                                    <li className="mb-2">Scratch kennenlernen</li>
                                    <li>Erste Animation</li>
                                </ul>
                                <span className="badge mt-2" style={{ background: '#ff6b35' }}>Materialien ansehen</span>
                            </div>
                        </div>
                    </a>
                </div>

                {/* Tag 2 */}
                <div className="col-md-6 col-lg-3">
                    <a href="/ostercamp-programmieren/tag-2/" className="text-decoration-none">
                        <div className="card h-100 hover-lift">
                            <div className="card-header text-white" style={{ background: '#9b59b6' }}>
                                <strong>Tag 2</strong>
                            </div>
                            <div className="card-body">
                                <h5 className="card-title" style={{ color: '#324047' }}>Interaktive Geschichten</h5>
                                <ul className="list-unstyled mb-0" style={{ color: '#324047' }}>
                                    <li className="mb-2">Schleifen verstehen</li>
                                    <li className="mb-2">Ereignisse nutzen</li>
                                    <li>Geschichte erzählen</li>
                                </ul>
                                <span className="badge mt-2" style={{ background: '#9b59b6' }}>Materialien ansehen</span>
                            </div>
                        </div>
                    </a>
                </div>

                {/* Tag 3 */}
                <div className="col-md-6 col-lg-3">
                    <a href="/ostercamp-programmieren/tag-3/" className="text-decoration-none">
                        <div className="card h-100 hover-lift">
                            <div className="card-header text-white" style={{ background: '#3498db' }}>
                                <strong>Tag 3</strong>
                            </div>
                            <div className="card-body">
                                <h5 className="card-title" style={{ color: '#324047' }}>Spiele entwickeln</h5>
                                <ul className="list-unstyled mb-0" style={{ color: '#324047' }}>
                                    <li className="mb-2">Variablen & Punkte</li>
                                    <li className="mb-2">Kollisionserkennung</li>
                                    <li>Fangspiel programmieren</li>
                                </ul>
                                <span className="badge mt-2" style={{ background: '#3498db' }}>Materialien ansehen</span>
                            </div>
                        </div>
                    </a>
                </div>

                {/* Tag 4 */}
                <div className="col-md-6 col-lg-3">
                    <a href="/ostercamp-programmieren/tag-4/" className="text-decoration-none">
                        <div className="card h-100 hover-lift">
                            <div className="card-header text-white" style={{ background: '#00d084' }}>
                                <strong>Tag 4</strong>
                            </div>
                            <div className="card-body">
                                <h5 className="card-title" style={{ color: '#324047' }}>Eigenes Spiel</h5>
                                <ul className="list-unstyled mb-0" style={{ color: '#324047' }}>
                                    <li className="mb-2">Eigenes Spiel planen</li>
                                    <li className="mb-2">Entwicklung & Test</li>
                                    <li>Präsentation & Zertifikat</li>
                                </ul>
                                <span className="badge mt-2" style={{ background: '#00d084' }}>Materialien ansehen</span>
                            </div>
                        </div>
                    </a>
                </div>
            </div>

            {/* Lernziele */}
            <div className="card mb-4">
                <div className="card-header">
                    <h4 className="mb-0">Lernziele</h4>
                </div>
                <div className="card-body">
                    <div className="row g-4">
                        <div className="col-md-6 col-lg-3">
                            <h6 style={{ color: '#ff6b35' }}>Programmieren verstehen</h6>
                            <ul className="small">
                                <li>Wie funktioniert Programmieren?</li>
                                <li>Befehle & Sequenzen</li>
                                <li>Schleifen & Wiederholungen</li>
                                <li>Ereignisse & Bedingungen</li>
                            </ul>
                        </div>
                        <div className="col-md-6 col-lg-3">
                            <h6 style={{ color: '#9b59b6' }}>Scratch meistern</h6>
                            <ul className="small">
                                <li>Blöcke richtig verbinden</li>
                                <li>Figuren animieren</li>
                                <li>Sounds & Musik einsetzen</li>
                                <li>Hintergründe gestalten</li>
                            </ul>
                        </div>
                        <div className="col-md-6 col-lg-3">
                            <h6 style={{ color: '#3498db' }}>Game Design</h6>
                            <ul className="small">
                                <li>Spielmechaniken verstehen</li>
                                <li>Variablen für Punkte</li>
                                <li>Kollisionserkennung</li>
                                <li>Spielregeln umsetzen</li>
                            </ul>
                        </div>
                        <div className="col-md-6 col-lg-3">
                            <h6 style={{ color: '#00d084' }}>Kreativität</h6>
                            <ul className="small">
                                <li>Eigene Ideen umsetzen</li>
                                <li>Figuren selbst gestalten</li>
                                <li>Geschichten erzählen</li>
                                <li>Projekte präsentieren</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tag 1 Details */}
            <div className="card mb-4" style={{ borderLeft: '4px solid #ff6b35' }}>
                <div className="card-header" style={{ background: 'rgba(255,107,53,0.1)' }}>
                    <h4 className="mb-0" style={{ color: '#ff6b35' }}>Tag 1 im Detail: Erste Schritte mit Scratch</h4>
                </div>
                <div className="card-body">
                    <div className="alert mb-4" style={{ background: '#fff3e0', borderLeft: '4px solid #ff6b35' }}>
                        <p className="mb-0">
                            <strong>Philosophie:</strong> Wir starten spielerisch und ohne Druck. Die Kinder lernen,
                            was Programmieren bedeutet und machen ihre ersten Schritte mit Scratch.
                        </p>
                    </div>
                    <div className="row g-3">
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: '#e8f5e9' }}>
                                <h6><span className="badge me-2" style={{ background: '#00d084' }}>09:00</span> PC-Grundlagen</h6>
                                <p className="small mb-0">
                                    Maus-Training, Tastatur, Sonderzeichen (@, €) und Groß-/Kleinschreibung.
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: '#f5f5f0' }}>
                                <h6><span className="badge me-2" style={{ background: '#ff6b35' }}>09:45</span> Was ist Programmieren?</h6>
                                <p className="small mb-0">
                                    Spielerische Einführung mit dem Roboter-Spiel. Kinder geben Befehle, andere führen aus.
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: '#f5f5f0' }}>
                                <h6><span className="badge me-2" style={{ background: '#ff6b35' }}>10:15</span> Scratch kennenlernen</h6>
                                <p className="small mb-0">
                                    Die Oberfläche erkunden, erste Blöcke entdecken und die Katze bewegen.
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: '#f5f5f0' }}>
                                <h6><span className="badge me-2" style={{ background: '#9b59b6' }}>10:45</span> Figuren bewegen</h6>
                                <p className="small mb-0">
                                    Bewegungsblöcke, Drehungen, Kostümwechsel und erste Soundeffekte.
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: '#00d084', color: 'white' }}>
                                <h6><span className="badge bg-white text-dark me-2">11:15</span> Pause</h6>
                                <p className="small mb-0">
                                    Snacks & Bewegung - Zeit zum Durchatmen!
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: '#f5f5f0' }}>
                                <h6><span className="badge me-2" style={{ background: '#f810b1' }}>11:45</span> Meine erste Animation</h6>
                                <p className="small mb-0">
                                    Eigene kleine Animation erstellen - die Katze tanzt oder erzählt eine Geschichte.
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: '#f5f5f0' }}>
                                <h6><span className="badge me-2" style={{ background: '#00d084' }}>12:30</span> Präsentation</h6>
                                <p className="small mb-0">
                                    Animationen vorführen und Ausblick auf morgen.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tag 2 Details */}
            <div className="card mb-4" style={{ borderLeft: '4px solid #9b59b6' }}>
                <div className="card-header" style={{ background: 'rgba(155,89,182,0.1)' }}>
                    <h4 className="mb-0" style={{ color: '#9b59b6' }}>Tag 2 im Detail: Interaktive Geschichten</h4>
                </div>
                <div className="card-body">
                    <div className="alert mb-4" style={{ background: '#f3e5f5', borderLeft: '4px solid #9b59b6' }}>
                        <p className="mb-0">
                            <strong>Projekt:</strong> Heute lernen wir Schleifen und Ereignisse kennen und erstellen
                            eine interaktive Geschichte mit mehreren Figuren und Dialogen.
                        </p>
                    </div>
                    <div className="row g-3">
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: '#f5f5f0' }}>
                                <h6><span className="badge me-2" style={{ background: '#9b59b6' }}>09:00</span> Wiederholung & Warm-up</h6>
                                <p className="small mb-0">
                                    Was haben wir gestern gelernt? Kurze Auffrischung mit Mini-Challenge.
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: '#f5f5f0' }}>
                                <h6><span className="badge me-2" style={{ background: '#9b59b6' }}>09:30</span> Schleifen verstehen</h6>
                                <p className="small mb-0">
                                    "Wiederhole 10 mal" - Schleifen machen das Programmieren einfacher!
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: '#f5f5f0' }}>
                                <h6><span className="badge me-2" style={{ background: '#9b59b6' }}>10:15</span> Ereignisse nutzen</h6>
                                <p className="small mb-0">
                                    Wenn Taste gedrückt, wenn angeklickt - Interaktion einbauen.
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: '#00d084', color: 'white' }}>
                                <h6><span className="badge bg-white text-dark me-2">10:45</span> Pause</h6>
                                <p className="small mb-0">
                                    Snacks & Erholung
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: '#f5f5f0' }}>
                                <h6><span className="badge me-2" style={{ background: '#9b59b6' }}>11:15</span> Geschichte erstellen</h6>
                                <p className="small mb-0">
                                    Eigene interaktive Geschichte mit Dialogen und mehreren Figuren.
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: '#f5f5f0' }}>
                                <h6><span className="badge me-2" style={{ background: '#9b59b6' }}>12:30</span> Präsentation</h6>
                                <p className="small mb-0">
                                    Geschichten vorführen und gemeinsam entdecken.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tag 3 Details */}
            <div className="card mb-4" style={{ borderLeft: '4px solid #3498db' }}>
                <div className="card-header" style={{ background: 'rgba(52,152,219,0.1)' }}>
                    <h4 className="mb-0" style={{ color: '#3498db' }}>Tag 3 im Detail: Spiele entwickeln</h4>
                </div>
                <div className="card-body">
                    <div className="alert mb-4" style={{ background: '#e3f2fd', borderLeft: '4px solid #3498db' }}>
                        <p className="mb-0">
                            <strong>Heute:</strong> Wir lernen Variablen kennen und bauen ein richtiges Spiel mit Punkten,
                            Kollisionserkennung und Spiellogik!
                        </p>
                    </div>
                    <div className="row g-3">
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: '#f5f5f0' }}>
                                <h6><span className="badge me-2" style={{ background: '#3498db' }}>09:00</span> Variablen verstehen</h6>
                                <p className="small mb-0">
                                    Was sind Variablen? Punkte zählen und anzeigen.
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: '#f5f5f0' }}>
                                <h6><span className="badge me-2" style={{ background: '#3498db' }}>09:45</span> Kollisionserkennung</h6>
                                <p className="small mb-0">
                                    "Berührt Farbe?" und "Berührt Figur?" - die Grundlage für jedes Spiel.
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: '#00d084', color: 'white' }}>
                                <h6><span className="badge bg-white text-dark me-2">10:30</span> Pause</h6>
                                <p className="small mb-0">
                                    Snacks & Erholung
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: '#f5f5f0' }}>
                                <h6><span className="badge me-2" style={{ background: '#3498db' }}>11:00</span> Fangspiel bauen</h6>
                                <p className="small mb-0">
                                    Gemeinsam ein Fangspiel programmieren - Schritt für Schritt.
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: '#f5f5f0' }}>
                                <h6><span className="badge me-2" style={{ background: '#3498db' }}>12:00</span> Spiel erweitern</h6>
                                <p className="small mb-0">
                                    Eigene Ideen einbauen: Level, Timer, mehr Gegner...
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: '#f5f5f0' }}>
                                <h6><span className="badge me-2" style={{ background: '#3498db' }}>12:30</span> Spielen & Testen</h6>
                                <p className="small mb-0">
                                    Die Spiele der anderen ausprobieren und Feedback geben.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tag 4 Details */}
            <div className="card mb-4" style={{ borderLeft: '4px solid #00d084' }}>
                <div className="card-header" style={{ background: 'rgba(0,208,132,0.1)' }}>
                    <h4 className="mb-0" style={{ color: '#00d084' }}>Tag 4 im Detail: Eigenes Spiel</h4>
                </div>
                <div className="card-body">
                    <div className="alert mb-4" style={{ background: '#e8f5e9', borderLeft: '4px solid #00d084' }}>
                        <p className="mb-0">
                            <strong>Projekttag!</strong> Heute setzen die Kinder ihr eigenes Spielprojekt um.
                            Sie planen, entwickeln und präsentieren ihr Spiel - und bekommen ein Zertifikat!
                        </p>
                    </div>
                    <div className="row g-3">
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: '#f5f5f0' }}>
                                <h6><span className="badge me-2" style={{ background: '#00d084' }}>09:00</span> Projekt planen</h6>
                                <p className="small mb-0">
                                    Welches Spiel? Ideen sammeln, Skizze machen, Plan erstellen.
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: '#f5f5f0' }}>
                                <h6><span className="badge me-2" style={{ background: '#00d084' }}>09:30</span> Entwicklung starten</h6>
                                <p className="small mb-0">
                                    Los geht's! Figuren erstellen, Code schreiben, testen.
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: '#00d084', color: 'white' }}>
                                <h6><span className="badge bg-white text-dark me-2">10:30</span> Pause</h6>
                                <p className="small mb-0">
                                    Snacks & Erholung
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: '#f5f5f0' }}>
                                <h6><span className="badge me-2" style={{ background: '#00d084' }}>11:00</span> Weiterentwickeln</h6>
                                <p className="small mb-0">
                                    Spiel fertigstellen, Details hinzufügen, testen und verbessern.
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: '#f5f5f0' }}>
                                <h6><span className="badge me-2" style={{ background: '#f810b1' }}>12:00</span> Präsentationen</h6>
                                <p className="small mb-0">
                                    Jeder stellt sein Spiel vor und lässt andere spielen!
                                </p>
                            </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                            <div className="p-3 rounded" style={{ background: 'linear-gradient(135deg, #00d084, #ff6b35)', color: 'white' }}>
                                <h6><span className="badge bg-white text-dark me-2">12:30</span> Zertifikat</h6>
                                <p className="small mb-0">
                                    Zertifikatsverleihung & Abschluss!
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Kostenlose Tools */}
            <div className="card mb-4">
                <div className="card-header">
                    <h4 className="mb-0">Online-Tools für den Workshop</h4>
                </div>
                <div className="card-body">
                    <div className="row g-3">
                        <div className="col-md-6 col-lg-3">
                            <h6 style={{ color: '#ff6b35' }}>Haupt-Werkzeug</h6>
                            <ul className="mb-0">
                                <li><a href="https://scratch.mit.edu" target="_blank" rel="noreferrer">Scratch</a> - Unsere Programmierumgebung</li>
                                <li><a href="https://scratch.mit.edu/projects/editor/" target="_blank" rel="noreferrer">Scratch Editor</a> - Direkt loslegen</li>
                            </ul>
                        </div>
                        <div className="col-md-6 col-lg-3">
                            <h6 style={{ color: '#9b59b6' }}>Bonus-Material</h6>
                            <ul className="mb-0">
                                <li><a href="https://www.code.org/hourofcode/overview" target="_blank" rel="noreferrer">Hour of Code</a> - Zusätzliche Übungen</li>
                                <li><a href="https://studio.code.org/s/dance/lessons/1/levels/1" target="_blank" rel="noreferrer">Dance Party</a> - Programmierte Tanzparty</li>
                            </ul>
                        </div>
                        <div className="col-md-6 col-lg-3">
                            <h6 style={{ color: '#3498db' }}>Inspiration</h6>
                            <ul className="mb-0">
                                <li><a href="https://scratch.mit.edu/explore/projects/games" target="_blank" rel="noreferrer">Scratch Games</a> - Spielinspiration</li>
                                <li><a href="https://scratch.mit.edu/studios/146521/" target="_blank" rel="noreferrer">Starter Games</a> - Einfache Spiele</li>
                            </ul>
                        </div>
                        <div className="col-md-6 col-lg-3">
                            <h6 style={{ color: '#00d084' }}>Tutorials</h6>
                            <ul className="mb-0">
                                <li><a href="https://scratch.mit.edu/projects/editor/?tutorial=getStarted" target="_blank" rel="noreferrer">Erste Schritte</a> - Offizielles Tutorial</li>
                                <li><a href="https://scratch.mit.edu/ideas" target="_blank" rel="noreferrer">Scratch Ideas</a> - Projektideen</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            {/* Hinweis für Eltern */}
            <div className="alert" style={{ background: 'rgba(255,107,53,0.15)', color: '#c04000' }}>
                <h5 className="alert-heading">Hinweis für Eltern</h5>
                <p className="mb-0">
                    Im Workshop lernen die Kinder spielerisch die Grundlagen des Programmierens mit Scratch.
                    Sie entwickeln logisches Denken, Problemlösungsfähigkeiten und Kreativität.
                    Scratch ist kostenlos und kann auch zuhause weiter genutzt werden!
                    Bei Fragen zum Workshop können Sie uns jederzeit kontaktieren.
                </p>
            </div>
        </div>
    );
}

export default ProgrammierenWorkshop;
